import { useCallback, type ComponentProps, type MouseEventHandler, type PropsWithChildren } from "react";
import { useNavigate } from "react-router";

import { classNames, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { NodeDesigner } from "../NodeDesigner";
import { useSetSelectedNodeId } from "../selection/useSelectedNode";
import type { DesignerNodeHandle } from "../types";
import { useContextNodeId } from "./context.ts";
import { Thumbnail } from "./Thumbnail";
import { useGetNodeRoutePath } from "./useGetNodeRoutePath";

export type NavigationThumbnailProps = ComponentProps<"div"> & {
  node: DesignerNodeHandle;
  title: string | undefined;
  selected?: boolean;
  tooltipProps?: Omit<ComponentProps<typeof Tooltip>, "children">;
};

export const NavigationThumbnail = ({
  node,
  title,
  selected = false,
  tooltipProps,
  children,
  ...thumbnailProps
}: PropsWithChildren<NavigationThumbnailProps>) => {
  const formatMessage = useFormatMessage(formatStrings);

  const navigate = useNavigate();
  const getNodeRoutePath = useGetNodeRoutePath();

  const contextNodeId = useContextNodeId();
  const setSelectedNodeId = useSetSelectedNodeId();

  const handleNavigate: MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation(); // otherwise passes through and selects canvas node (e.g. scene)

      const routePath = getNodeRoutePath(node.id);
      setSelectedNodeId(contextNodeId);
      navigate(routePath)?.catch(console.warn);
    },
    [contextNodeId, getNodeRoutePath, navigate, node.id, setSelectedNodeId],
  );

  return (
    <Tooltip {...tooltipProps} title={tooltipProps?.title || title || formatMessage("fallbackTitle")}>
      <Thumbnail
        {...thumbnailProps}
        onClick={handleNavigate}
        className={classNames({ selected })}
        data-test="thumbnail"
      >
        <NodeDesigner node={node} view="thumbnail" />
        {children}
      </Thumbnail>
    </Tooltip>
  );
};

const formatStrings = {
  fallbackTitle: {
    en: "Untitled",
  },
} satisfies FormatMessages;
